<template>
    <div class="form-group vue-select position-relative" v-click-outside="() => (opened = false)">
        <label
            :class="{
                'text-danger': errors && errors.length,
                'text-dark': !errors || !errors.length,
                required: required
            }"
            :for="id"
            v-if="label"
        >
            <small v-if="labelSmall">{{ label }}</small>
            <template v-else>{{ label }}</template>
        </label>

        <input
            type="text"
            class="input form-control rounded text-muted minimal"
            v-on:click="toggle"
            ref="input"
            :placeholder="placeholder"
            v-on:input="searching($event)"
            :disabled="disabled"
            v-model="inputValue"
            :class="{ 'is-invalid': errors && errors.length, loading: loading }"
        />

        <div class="options shadow position-absolute w-100" v-if="opened">
            <div
                v-for="item in currentOptions"
                :key="`${id}-option-${dataValue(item)}`"
                v-html="dataText(item)"
                v-on:click="setOption(item)"
                class="form-control form-control-sm"
                :class="{ selected: isSelected(dataValue(item)) }"
            />

            <div class="form-control text-center text-muted" v-if="!currentOptions.length && searchText.length > 0">
                There are no records containing <strong>{{ searchText }}</strong>
            </div>

            <div
                class="form-control d-flex justify-content-between align-items-center px-0"
                v-if="showPaginator && hasMoreItems"
            >
                <span
                    class="p-2 paginator"
                    :class="{ active: pageExists('previous') }"
                    v-on:click="goToPage(currentPage - 1)"
                >
                    <i class="fa fa-chevron-left" />
                </span>

                <small class="text-muted">Type to search for more</small>

                <span
                    class="p-2 paginator"
                    :class="{ active: pageExists('next') }"
                    v-on:click="goToPage(currentPage + 1)"
                >
                    <i class="fa fa-chevron-right" />
                </span>
            </div>
        </div>

        <small class="text-danger font-italic validation-error" style="font-size: 12px" v-if="errors && errors.length">
            {{ errors[0] }}
        </small>
    </div>
</template>

<script>
    import Vue from 'vue';

    import ClickOutside from 'vue-click-outside';

    export default Vue.extend({
        directives: {
            ClickOutside
        },
        props: {
            value: null,
            loading: Boolean,
            required: Boolean,
            readonly: Boolean,
            disabled: Boolean,
            errors: Array,
            label: String,
            labelSmall: {
                default: true,
                type: Boolean
            },
            data: {
                required: true,
                type: Array | Object
            },
            dataValue: {
                default: (item) => item.id,
                type: Function
            },
            dataText: {
                default: (item) => item.text,
                type: Function
            },
            placeholder: {
                default: 'Select an option',
                type: String
            },
            showPaginator: {
                default: true,
                type: Boolean
            },
            perPage: {
                default: 10,
                type: Number
            }
        },
        data() {
            return {
                id: `text-input-${this.uid}`,
                opened: false,
                paginator: true,
                multiple: false,
                inputText: '',
                searchText: '',
                currentPage: 1,
                selectedOptions: []
            };
        },
        watch: {
            data: {
                deep: true,
                immediate: true,
                handler() {
                    const type = this.data.constructor.name;

                    this.paginator = true;

                    if (type != 'Object' || this.data.total === undefined) {
                        this.paginator = false;
                    }

                    if (this.paginator) {
                        this.currentPage = this.data.current_page;
                    }

                    this.setValue();
                }
            },
            selectedOptions: {
                deep: true,
                immediate: true,
                handler() {
                    this.inputText = '';

                    this.selectedOptions.forEach((item, index) => {
                        this.inputText += this.dataText(item);

                        if (index < this.selectedOptions.length - 1) {
                            this.inputText += ', ';
                        }
                    });
                }
            }
        },
        computed: {
            hasMoreItems() {
                if (!this.paginator && this.data.constructor.name == 'Array' && this.data.length < this.perPage) {
                    return false;
                } else if (!this.paginator) {
                    return true;
                }

                return this.data.last_page > 1;
            },
            currentOptions() {
                if (this.paginator) {
                    return this.data.data;
                }

                return this.data.slice((this.currentPage - 1) * this.perPage, this.currentPage * this.perPage);
            },
            inputValue: {
                get() {
                    return this.opened ? this.searchText : this.inputText;
                },
                set(value) {
                    if (this.opened) {
                        this.searchText = value;
                    } else {
                        this.inputText = value;
                    }
                }
            }
        },
        methods: {
            toggle() {
                this.opened = !this.opened;
            },
            setOption(item) {
                const value = this.dataValue(item);
                const checked = this.selectedOptions.find((s) => this.dataValue(s) == value);

                if (checked) {
                    this.selectedOptions = this.selectedOptions.filter((s) => this.dataValue(s) != value);

                    if (!this.multiple) {
                        this.$emit('input', null);
                        this.$emit('change', null);
                    }
                } else {
                    if (!this.multiple) {
                        this.selectedOptions = this.selectedOptions.filter((s) => this.dataValue(s) == value);
                        this.$emit('input', this.dataValue(item));
                        this.$emit('change', this.dataValue(item));
                    }

                    this.selectedOptions.push(JSON.parse(JSON.stringify(item)));
                }

                this.opened = false;
            },
            isSelected(id) {
                return this.selectedOptions.find((s) => this.dataValue(s) == id);
            },
            pageExists(type) {
                if (type == 'next' && this.paginator) {
                    return this.data.current_page < this.data.last_page;
                } else if (type == 'next') {
                    return this.data.length > this.currentPage * this.perPage;
                }

                if (type == 'previous' && this.paginator) {
                    return this.data.current_page > 1;
                } else if (type == 'previous') {
                    return this.current_page > 1;
                }
            },
            goToPage(page) {
                if (page < this.currentPage && this.pageExists('previous')) {
                    this.currentPage = page;
                }

                if (page > this.currentPage && this.pageExists('next')) {
                    this.currentPage = page;
                }

                if (this.currentPage == page) {
                    this.$emit('page-changed', page);
                }
            },
            searching(event) {
                const keyword = event.target.value;

                this.opened = true;

                this.$emit('search', keyword);
            },
            setValue() {
                if (!this.value || !this.value.constructor || !this.data) {
                    return;
                }

                if (this.paginator && (!this.data.data || !this.data.data.length)) {
                    return;
                }

                if (!this.paginator && !this.data.length) {
                    return;
                }

                if (this.value.constructor.name != 'Array') {
                    const option = this.paginator
                        ? this.data.data.find((i) => this.dataValue(i) == this.value)
                        : this.data.find((i) => this.dataValue(i) == this.value);

                    if (!option) {
                        return;
                    }

                    this.selectedOptions.push(option);
                }
            }
        },
        created() {
            window.addEventListener('scroll', () => {
                this.opened = false;
            });
        }
    });
</script>
